import { Formik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { FC, useRef, useState } from 'react';
import { TFunction } from 'react-i18next';

import { RegistrationFormData } from 'features/registration/interface/registration';
import { dataLayerEventsForRegBack, dataLayerEventsForRegNext } from 'features/registration/utility/utility';
import FormContainer from 'shared/components/dialog/FormContainer';
import Button from 'shared/components/form/button';
import FormButtons from 'shared/components/form/formButtons';
import { ActivationType, sendEventsToDataLayer } from 'shared/constants/datalayer';
import { getRegistrationFieldValidators } from 'shared/constants/validation-schema';
import { isFieldValueValid } from 'shared/util/utility';
import { registrationFormSteps } from '../RegistrationSteps';
import SelectConsentDialog from '../SelectConsentDialog';

import { useSendSmsCode } from 'features/registration/hooks/useSendSmsCode';
import WebView from 'features/webView/utils';
import { findCookieValue } from 'shared/util/utility';
import { RegistrationApi } from '../../../../api';
import { UNIQUE_FIELD_VALIDATION_DEBOUNCE } from '../../../../shared/constants/common';
import { asyncDebounce } from '../../../../shared/hooks/useAsyncDebounce';
import './styles.scss';

interface Props {
	currentStep: number;
	numberOfSteps: number;
	emailVerification: boolean;
	scratchedCode: boolean;
	initialValues: RegistrationFormData;
	isDesktop?: boolean;
	loading: boolean;
	setEmailVerification: (emailVerification: boolean) => void;
	setScratchedCode: (scratchedCode: boolean) => void;
	handlePrevStep: (formData: RegistrationFormData) => void;
	handleNextStep: (formData: RegistrationFormData) => void;
	handleCloseRegistrationProcess?: () => void;
	onSubmit: (data: RegistrationFormData) => void;
	t: TFunction<'translation'>;
}

type DialogType = '' | 'consentsDialog';

const RegistrationForm: FC<Props> = ({
	currentStep,
	numberOfSteps,
	emailVerification,
	initialValues,
	isDesktop,
	loading,
	setEmailVerification,
	handlePrevStep,
	handleNextStep,
	handleCloseRegistrationProcess,
	onSubmit,
	setScratchedCode,
	scratchedCode,
	t,
}) => {
	const [dialogType, setDialogType] = useState<DialogType>('');
	const [showConsentPopup, setShowConsentPopup] = useState(false);
	const { sendCode } = useSendSmsCode(setDialogType);
	const [consentBefore, setConsentBefore] = useState<number>();

	const isSubmit = (values: RegistrationFormData) =>
		currentStep === numberOfSteps && ((showConsentPopup && dialogType === '') || values.selectAllConsents);

	const triggerConsentDialog = (values: RegistrationFormData) =>
		currentStep === numberOfSteps && (!values.emailConsent || !values.smsConsent || !values.allConsents) && !showConsentPopup;
	const accountVerificationType: ActivationType = emailVerification ? 'email' : 'phone';

	const getConsentNumber = (emailConsent: boolean, smsConsent: boolean, allConsents: boolean) => {
		if (allConsents || (emailConsent && smsConsent)) return 2;
		else if (emailConsent || smsConsent) return 1;
		else return 0;
	};

	const cookiePromoCode = findCookieValue('promo');

	const getSubHeadingContent = (step: number) => {
		switch (step) {
			case 8:
				return cookiePromoCode ? 'regStep8SubHeadingAlt' : 'regStep8SubHeading';
			case 6:
				return emailVerification ? 'regStep6SubHeadingAlt' : 'regStep6SubHeading';
			default:
				return `regStep${currentStep}SubHeading`;
		}
	};
	const checkUniquenessDebounce = useRef(asyncDebounce(RegistrationApi.checkUniqueness, UNIQUE_FIELD_VALIDATION_DEBOUNCE));
	return (
		<>
			<FormContainer
				currentStep={currentStep}
				numberOfSteps={numberOfSteps}
				textualContent={{
					heading: `regStep${currentStep}Heading`,
					subHeading: getSubHeadingContent(currentStep),
				}}
				t={t}
				isDesktop={isDesktop}
				closeDialog={handleCloseRegistrationProcess}
			>
				<Formik
					enableReinitialize={true}
					initialValues={initialValues}
					validationSchema={() => getRegistrationFieldValidators(currentStep, checkUniquenessDebounce)}
					validateOnBlur={false}
					validateOnChange={true}
					onSubmit={onSubmit}
				>
					{({
						handleBlur,
						setFieldValue,
						values,
						errors,
						touched,
						setFieldError,
						setFieldTouched,
						setValues,
						validateForm,
						isValidating,
					}) => {
						const { allConsents, emailConsent, smsConsent } = values;
						const handleNextStepChange = () => {
							if (triggerConsentDialog(values)) {
								setConsentBefore(getConsentNumber(emailConsent, smsConsent, allConsents));
								setShowConsentPopup(true);
								setDialogType('consentsDialog');
								return;
							}
							validateForm().then((error) => {
								if (isEmpty(error)) {
									// send sms code after correct phoneNum and when emailverification is not checked
									currentStep === 6 &&
										!emailVerification &&
										sendCode(values.email, values.emailConsent, values.phoneNum, values.smsConsent);

									handleNextStep(values);
									sendEventsToDataLayer(
										dataLayerEventsForRegNext(
											accountVerificationType,
											values.email,
											allConsents,
											scratchedCode,
											consentBefore,
											getConsentNumber(emailConsent, smsConsent, allConsents),
											showConsentPopup,
											'manual',
										),
										currentStep,
									);
									WebView.broadcastAnalytics('REGISTRATION_COMPLETED');
								}
							});
						};

						const handlePrevStepChange = () => {
							handlePrevStep(values);
							sendEventsToDataLayer(dataLayerEventsForRegBack, currentStep);
						};

						return (
							<form
								className="RegistrationForm__form !overflow-y-auto"
								onSubmit={(e) => {
									e.preventDefault();
									handleNextStepChange();
								}}
							>
								<div>
									{registrationFormSteps.map(
										(RegistrationForm, index) =>
											currentStep === index + 1 && (
												<RegistrationForm
													key={`reg-form-${index}`}
													t={t}
													values={values}
													setValues={setValues}
													errors={errors}
													touched={touched}
													setFieldError={setFieldError}
													setFieldTouched={setFieldTouched}
													handleBlur={handleBlur}
													setFieldValue={setFieldValue}
													isFieldValueValid={isFieldValueValid}
													emailVerification={emailVerification}
													setEmailVerification={setEmailVerification}
													scratchedCode={scratchedCode}
													setScratchedCode={setScratchedCode}
												/>
											),
									)}
								</div>
								<FormButtons>
									<Button
										btnType="outline-white"
										type="button"
										onClick={() => {
											handlePrevStepChange();
										}}
										cyTestRef="registration-back"
										className="RegistrationForm__button_back"
									>
										{currentStep === 1 ? t('quit') : t('back')}
									</Button>
									<Button btnType="secondary" type="submit" loading={loading || isValidating} cyTestRef="registration-forward">
										{isSubmit(values) ? t('finish') : t('further')}
									</Button>
								</FormButtons>
								{dialogType === 'consentsDialog' && (
									<SelectConsentDialog
										t={t}
										onClose={() => {
											setFieldValue('ageConsent', true);
											setFieldError('ageConsent', '');
											setDialogType('');
										}}
										onAcceptConsents={() => {
											setValues({
												...values,
												emailConsent: true,
												smsConsent: true,
												allConsents: true,
												ageConsent: true,
												selectAllConsents: true,
											});
											setFieldError('ageConsent', '');
											setDialogType('');
										}}
									/>
								)}
							</form>
						);
					}}
				</Formik>
			</FormContainer>
		</>
	);
};

export default RegistrationForm;
